import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, CheckCircle, Clock, XCircle, IndianRupee, Eye, Edit2, Trash2, Download, Package, MoreVertical } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { purchaseOrdersAPI } from '../../services/part2-api';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const getSupplierName = (o) => o.supplier_name || o.party_name || '';
const getExpectedDate = (o) => o.expected_delivery_date || o.expected_date || '';

const getStatusBadge = (status) => {
  const badges = {
    DRAFT: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
    draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
    CONFIRMED: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Confirmed' },
    confirmed: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Confirmed' },
    PARTIALLY_RECEIVED: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Partial' },
    RECEIVED: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Received' },
    received: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Received' },
    CLOSED: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Closed' },
    closed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Closed' },
    CANCELLED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' },
  };
  return badges[status] || badges.DRAFT;
};

const PurchaseOrdersPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, draft: 0, confirmed: 0, received: 0, cancelled: 0, total_value: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmModal, setConfirmModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const [columnFilters, setColumnFilters] = useState({
    order_number: '',
    supplier_name: '',
    order_date: { min: '', max: '' },
    expected_date: { min: '', max: '' },
    status: [],
    amount: { min: '', max: '' }
  });

  const bid = businessId || activeBusiness?.id;

  const fetchOrders = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter.toUpperCase();
      if (searchQuery) params.search = searchQuery;

      const response = await purchaseOrdersAPI.list(params);
      setOrders(response.data.orders || []);
      setStats(response.data.stats || { total: 0, draft: 0, confirmed: 0, received: 0, cancelled: 0, total_value: 0 });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load purchase orders'));
    } finally {
      setLoading(false);
    }
  }, [bid, statusFilter, searchQuery]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const filteredOrders = orders.filter(order => {
    const matchesOrderNo = !columnFilters.order_number ||
      order.order_number?.toLowerCase().includes(columnFilters.order_number.toLowerCase());
    const matchesSupplier = !columnFilters.supplier_name ||
      getSupplierName(order).toLowerCase().includes(columnFilters.supplier_name.toLowerCase());
    const matchesOrderDate = (!columnFilters.order_date?.min || new Date(order.order_date) >= new Date(columnFilters.order_date.min)) &&
      (!columnFilters.order_date?.max || new Date(order.order_date) <= new Date(columnFilters.order_date.max));
    const matchesExpectedDate = (!columnFilters.expected_date?.min || new Date(getExpectedDate(order)) >= new Date(columnFilters.expected_date.min)) &&
      (!columnFilters.expected_date?.max || new Date(getExpectedDate(order)) <= new Date(columnFilters.expected_date.max));
    const matchesStatus = columnFilters.status.length === 0 ||
      columnFilters.status.includes(order.status?.toUpperCase());
    const matchesAmount = (!columnFilters.amount?.min || (order.grand_total || 0) >= parseFloat(columnFilters.amount.min)) &&
      (!columnFilters.amount?.max || (order.grand_total || 0) <= parseFloat(columnFilters.amount.max));
    return matchesOrderNo && matchesSupplier && matchesOrderDate && matchesExpectedDate && matchesStatus && matchesAmount;
  });

  const handleView = (order) => navigate(`/business/${bid}/purchase-orders/${order.id}`);
  const handleEdit = (order) => navigate(`/business/${bid}/purchase-orders/${order.id}/edit`);

  const handleConfirm = (order) => {
    setConfirmModal({
      title: 'Confirm Purchase Order',
      description: `Confirm ${order.order_number}? This will lock the order for editing.`,
      confirmLabel: 'Confirm Order',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: CheckCircle,
      iconBgClass: 'bg-blue-100',
      iconClass: 'text-blue-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await purchaseOrdersAPI.confirm(order.id);
          toast.success('Purchase order confirmed');
          fetchOrders();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to confirm order'));
        }
      },
    });
  };

  const handleCancel = (order) => {
    setCancelReason('');
    setConfirmModal({
      title: 'Cancel Purchase Order',
      description: `Cancel ${order.order_number}? This action cannot be undone.`,
      confirmLabel: 'Cancel Order',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      isCancelFlow: true,
      onConfirm: async (reason) => {
        setConfirmModal(null);
        try {
          await purchaseOrdersAPI.cancel(order.id, reason);
          toast.success('Purchase order cancelled');
          setCancelReason('');
          fetchOrders();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to cancel order'));
        }
      },
    });
  };

  const handleDelete = (order) => {
    setConfirmModal({
      title: 'Delete Purchase Order',
      description: `Delete ${order.order_number}? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await purchaseOrdersAPI.delete(order.id);
          toast.success('Purchase order deleted');
          fetchOrders();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete order'));
        }
      },
    });
  };

  const handleDownload = async (order) => {
    try {
      const response = await purchaseOrdersAPI.pdf(order.id);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PO_${order.order_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `\u20B9${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `\u20B9${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `\u20B9${(amount / 1000).toFixed(1)}K`;
    return `\u20B9${amount?.toFixed(0) || 0}`;
  };

  const isTerminal = (s) => ['CANCELLED', 'cancelled', 'RECEIVED', 'received', 'CLOSED', 'closed'].includes(s);
  const isDraft = (s) => ['DRAFT', 'draft'].includes(s);
  const isConfirmed = (s) => ['CONFIRMED', 'confirmed'].includes(s);

  return (
    <div className="h-full flex flex-col">
      <ListPageHeader
        title="Purchase Orders"
        stats={[
          { icon: FileText, label: 'Total', value: stats.total, color: 'slate' },
          { icon: Clock, label: 'Draft', value: stats.draft, color: 'amber' },
          { icon: CheckCircle, label: 'Confirmed', value: stats.confirmed, color: 'blue' },
          { icon: Package, label: 'Received', value: stats.received, color: 'emerald' },
          { icon: IndianRupee, label: 'Order Value', value: formatCompactCurrency(stats.total_value), color: 'purple' },
        ]}
        ctaLabel="New Purchase Order"
        onCtaClick={() => navigate(`/business/${bid}/purchase-orders/new`)}
      />

      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">{searchQuery ? 'No orders found' : 'No purchase orders yet'}</p>
              <p className="text-[13px] text-slate-400 mt-1 mb-4">{searchQuery ? 'Try adjusting your search' : 'Create your first purchase order'}</p>
              {!searchQuery && (
                <button onClick={() => navigate(`/business/${bid}/purchase-orders/new`)} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-[13px] font-medium">
                  Create Purchase Order
                </button>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-shrink-0">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '12%' }}>
                        <div className="flex items-center gap-1">PO #
                          <ColumnFilter type="text" value={columnFilters.order_number} onChange={(val) => setColumnFilters({...columnFilters, order_number: val})} placeholder="Filter PO..." />
                        </div>
                      </th>
                      <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '22%' }}>
                        <div className="flex items-center gap-1">Supplier
                          <ColumnFilter type="text" value={columnFilters.supplier_name} onChange={(val) => setColumnFilters({...columnFilters, supplier_name: val})} placeholder="Filter supplier..." />
                        </div>
                      </th>
                      <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '12%' }}>
                        <div className="flex items-center gap-1">Order Date
                          <ColumnFilter type="date" value={columnFilters.order_date} onChange={(val) => setColumnFilters({...columnFilters, order_date: val})} />
                        </div>
                      </th>
                      <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '12%' }}>
                        <div className="flex items-center gap-1">Expected Delivery
                          <ColumnFilter type="date" value={columnFilters.expected_date} onChange={(val) => setColumnFilters({...columnFilters, expected_date: val})} />
                        </div>
                      </th>
                      <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '10%' }}>
                        <div className="flex items-center gap-1">Status
                          <ColumnFilter type="status" value={columnFilters.status} onChange={(val) => setColumnFilters({...columnFilters, status: val})} options={[
                            { value: 'DRAFT', label: 'Draft' }, { value: 'CONFIRMED', label: 'Confirmed' },
                            { value: 'RECEIVED', label: 'Received' }, { value: 'CANCELLED', label: 'Cancelled' }
                          ]} />
                        </div>
                      </th>
                      <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100 tl-th-right" style={{ width: '14%' }}>
                        <div className="flex items-center justify-end gap-1">Amount
                          <ColumnFilter type="number" value={columnFilters.amount} onChange={(val) => setColumnFilters({...columnFilters, amount: val})} />
                        </div>
                      </th>
                      <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '5%' }}></th>
                    </tr>
                  </thead>
                </table>
              </div>

              <div className="flex-1 overflow-y-auto">
                <table className="tl-table">
                  <tbody>
                    {filteredOrders.map((order, index) => {
                      const statusBadge = getStatusBadge(order.status);
                      return (
                        <tr key={order.id} className={`tl-tr tl-tr-clickable hover:bg-teal-50/40 ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`} onClick={() => handleView(order)}>
                          <td className="tl-td" style={{ width: '12%' }}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-[12px] font-medium text-teal-700">{order.order_number?.slice(-2) || 'PO'}</span>
                              </div>
                              <span className="tl-cell-primary">{order.order_number}</span>
                            </div>
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '22%' }}>{getSupplierName(order) || '-'}</td>
                          <td className="tl-td tl-cell-regular" style={{ width: '12%' }}>{order.order_date ? new Date(order.order_date).toLocaleDateString('en-IN') : '-'}</td>
                          <td className="tl-td tl-cell-regular" style={{ width: '12%' }}>{getExpectedDate(order) ? new Date(getExpectedDate(order)).toLocaleDateString('en-IN') : '-'}</td>
                          <td className="tl-td" style={{ width: '10%' }}>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadge.bg} ${statusBadge.text}`}>{statusBadge.label}</span>
                          </td>
                          <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '14%' }}>{'\u20B9'}{(order.grand_total || 0).toLocaleString('en-IN')}</td>
                          <td className="tl-td" style={{ width: '5%' }} onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors">
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => handleView(order)}>
                                  <Eye className="w-4 h-4 mr-2" /> View Details
                                </DropdownMenuItem>
                                {isDraft(order.status) && (
                                  <DropdownMenuItem onClick={() => handleEdit(order)}>
                                    <Edit2 className="w-4 h-4 mr-2" /> Edit
                                  </DropdownMenuItem>
                                )}
                                {isDraft(order.status) && (
                                  <DropdownMenuItem onClick={() => handleConfirm(order)}>
                                    <CheckCircle className="w-4 h-4 mr-2" /> Confirm
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => handleDownload(order)}>
                                  <Download className="w-4 h-4 mr-2" /> Download PDF
                                </DropdownMenuItem>
                                {isConfirmed(order.status) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleCancel(order)} className="text-red-600 focus:text-red-600">
                                      <XCircle className="w-4 h-4 mr-2" /> Cancel Order
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {isDraft(order.status) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDelete(order)} className="text-red-600 focus:text-red-600">
                                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>Showing <span className="font-medium text-slate-700">{filteredOrders.length}</span> orders</p>
                <p>Total Value: <span className="font-medium text-slate-700">{formatCompactCurrency(stats.total_value)}</span></p>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        confirmDisabled={confirmModal?.isCancelFlow ? !cancelReason.trim() : false}
        onConfirm={() => confirmModal?.onConfirm?.(cancelReason)}
        onClose={() => { setConfirmModal(null); setCancelReason(''); }}
      >
        {confirmModal?.isCancelFlow && (
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="e.g. Supplier unable to deliver, found better pricing, order no longer needed..."
            rows={3}
            className="w-full mt-3 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 focus:outline-none"
          />
        )}
      </ConfirmModal>
    </div>
  );
};

export default PurchaseOrdersPage;
